import {
	Component,
	OnInit,
	OnDestroy,
	signal,
	ViewChild,
	ElementRef,
	AfterViewInit,
	inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
	ReactiveFormsModule,
	FormBuilder,
	FormGroup,
	FormArray,
	FormControl,
	Validators,
} from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { ServiceCategoryService, ServiceCategory } from '../../../services/service-category';
import { AdminHeader } from '../../../services/admin-header';
import { ImageCropperModal } from '../../../components/image-cropper-modal/image-cropper-modal';
import Quill from 'quill';

interface Service {
	id: number;
	category_id: number;
	service_name: string;
	tagline: string;
	description: string;
	short_description: string;
	pricing: number;
	duration_minutes: number;
	is_consultation: boolean;
	display_order: number;
	status: boolean;
	image_name: string;
	image_path: string;
}

@Component({
	selector: 'app-add-service',
	imports: [CommonModule, ReactiveFormsModule, ImageCropperModal],
	templateUrl: './add-service.html',
	styleUrl: './add-service.css',
})
export class AddService implements OnInit, AfterViewInit, OnDestroy {
	private categoryService = inject(ServiceCategoryService);
	private adminHeader = inject(AdminHeader);

	serviceForm: FormGroup;
	isEditMode = signal(false);
	isSubmitting = signal(false);
	errorMessage = signal('');
	successMessage = signal('');
	imagePreview = signal<string | null>(null);
	selectedFile: File | null = null;
	serviceId: number | null = null;
	categories = signal<ServiceCategory[]>([]);

	// Other services, offered as options for the "Related Services" block
	allServices = signal<Service[]>([]);

	// Which detail-page sections are expanded in the editor
	openSection = signal<string | null>('seo');

	@ViewChild('quillEditor', { static: false }) quillEditor!: ElementRef;
	@ViewChild('imageCropper') imageCropper!: ImageCropperModal;
	quillInstance: Quill | null = null;
	selectedImageForCrop: File | null = null;

	// Preview data
	previewData = signal({
		service_name: '',
		short_description: '',
		description: '',
		pricing: 0,
		image_path: '',
	});

	constructor(
		private fb: FormBuilder,
		private http: HttpClient,
		private router: Router,
		private route: ActivatedRoute,
	) {
		this.serviceForm = this.fb.group({
			category_id: ['', Validators.required],
			service_name: ['', Validators.required],
			tagline: ['', Validators.required],
			short_description: ['', Validators.required],
			description: ['', Validators.required],
			pricing: [0, [Validators.min(0)]],
			duration_minutes: [30, [Validators.required, Validators.min(15)]],
			is_consultation: [false],
			is_featured: [false],
			display_order: [0],
			image: [null, Validators.required],

			// ─── Detail page content (see the website copy doc) ───
			meta_title: [''],
			meta_description: [''],
			intro: [''],
			why_us_title: [''],
			why_us_points: this.fb.array([]),
			concerns_title: [''],
			concerns: this.fb.array([]),
			how_it_works_title: [''],
			how_it_works: this.fb.array([]),
			who_its_for: [''],
			faqs: this.fb.array([]),
			related_service_ids: [[] as number[]],
			cta_text: [''],
			cta_button_label: [''],
		});

		// Subscribe to form changes for real-time preview
		this.serviceForm.valueChanges.subscribe((values) => {
			this.updatePreview(values);
		});
	}

	ngOnInit() {
		// Load categories
		this.loadCategories();

		// Options for the "Related Services" block
		this.loadAllServices();

		// Check if we're in edit mode
		this.route.params.subscribe((params) => {
			if (params['id']) {
				this.serviceId = +params['id'];
				this.isEditMode.set(true);
				this.loadServiceData(this.serviceId);
			} else {
				// Start a new service with the block sizes the copy doc uses,
				// so the admin fills in a template rather than an empty page.
				this.seedDetailBlocks();
			}
		});

		// Setup header actions
		this.setupHeaderActions();
	}

	ngOnDestroy() {
		this.adminHeader.clearHeaderConfig();
	}

	private setupHeaderActions() {
		this.adminHeader.setHeaderConfig({
			backButton: {
				show: true,
				callback: () => this.cancel(),
			},
			actions: [
				{
					label: this.isEditMode() ? 'Update Service' : 'Add Service',
					icon: 'M5 13l4 4L19 7',
					callback: () => this.submitForm(),
					variant: 'primary',
					disabled: this.isSubmitting(),
				},
			],
		});
	}

	loadCategories() {
		this.categoryService.getAll().subscribe({
			next: (res) => {
				if (res.status) this.categories.set(res.data);
			},
		});
	}

	ngAfterViewInit() {
		// Initialize Quill editor
		if (this.quillEditor) {
			this.quillInstance = new Quill(this.quillEditor.nativeElement, {
				theme: 'snow',
				modules: {
					toolbar: [
						['bold', 'italic', 'underline'],
						[{ list: 'ordered' }, { list: 'bullet' }],
						[{ align: [] }],
						['clean'],
					],
				},
				placeholder: 'Enter detailed description...',
			});

			// Listen to text changes
			this.quillInstance.on('text-change', () => {
				const html = this.quillInstance?.root.innerHTML || '';
				this.serviceForm.patchValue({ description: html }, { emitEvent: false });
				this.updatePreview(this.serviceForm.value);
			});
		}
	}

	loadServiceData(id: number) {
		const apiUrl = `${environment.apiUrl}/services/${id}`;
		this.http.get<any>(apiUrl).subscribe({
			next: (response) => {
				if (response.status && response.data) {
					const service = response.data;
					this.serviceForm.patchValue({
						category_id: service.category_id,
						service_name: service.service_name,
						tagline: service.tagline,
						short_description: service.short_description,
						description: service.description,
						pricing: service.pricing,
						duration_minutes: service.duration_minutes || 30,
						is_consultation: service.is_consultation || false,
						is_featured: service.is_featured || false,
						display_order: service.display_order || 0,
					});

					this.patchDetailFields(service);

					if (service.image_path) {
						this.imagePreview.set(this.getImageUrl(service.image_path));
						this.serviceForm.get('image')?.clearValidators();
						this.serviceForm.get('image')?.updateValueAndValidity();
					}

					// Set Quill editor content if it's already initialized
					if (this.quillInstance && service.description) {
						this.quillInstance.root.innerHTML = service.description;
					}

					this.updatePreview(this.serviceForm.value);
				}
			},
			error: (error) => {
				console.error('Error loading service:', error);
				this.errorMessage.set('Failed to load service data');
			},
		});
	}

	// ─────────────────────────────────────────────────────────────
	// Detail page content blocks
	// ─────────────────────────────────────────────────────────────

	get whyUsPoints(): FormArray {
		return this.serviceForm.get('why_us_points') as FormArray;
	}

	get concerns(): FormArray {
		return this.serviceForm.get('concerns') as FormArray;
	}

	get howItWorks(): FormArray {
		return this.serviceForm.get('how_it_works') as FormArray;
	}

	get faqs(): FormArray {
		return this.serviceForm.get('faqs') as FormArray;
	}

	toggleSection(section: string) {
		this.openSection.set(this.openSection() === section ? null : section);
	}

	/** The slug the public page will live at, shown as a hint in the editor. */
	previewSlug(): string {
		const name: string = this.serviceForm.get('service_name')?.value || '';
		return (
			name
				.toLowerCase()
				.trim()
				.replace(/[^a-z0-9]+/g, '-')
				.replace(/^-+|-+$/g, '') || 'service-slug'
		);
	}

	/** Placeholder for the "Why..." heading, matching the copy doc's wording. */
	defaultWhyUsTitle(): string {
		const name: string = this.serviceForm.get('service_name')?.value || 'this service';
		return `Why The OG Hair Studio for ${name}`;
	}

	addWhyUsPoint(value = '') {
		// Detail content is optional as a whole, so rows carry no required
		// validator — an empty template must never block saving the service.
		this.whyUsPoints.push(new FormControl(value));
	}

	addConcern(value: { title?: string; description?: string } = {}) {
		this.concerns.push(
			this.fb.group({
				title: [value.title ?? ''],
				description: [value.description ?? ''],
			}),
		);
	}

	addStep(value: { title?: string; description?: string } = {}) {
		this.howItWorks.push(
			this.fb.group({
				title: [value.title ?? ''],
				description: [value.description ?? ''],
			}),
		);
	}

	addFaq(value: { question?: string; answer?: string } = {}) {
		this.faqs.push(
			this.fb.group({
				question: [value.question ?? ''],
				answer: [value.answer ?? ''],
			}),
		);
	}

	removeAt(array: FormArray, index: number) {
		array.removeAt(index);
	}

	/** Move a repeater row up or down so the admin controls the display order. */
	moveItem(array: FormArray, index: number, direction: -1 | 1) {
		const target = index + direction;
		if (target < 0 || target >= array.length) {
			return;
		}
		const control = array.at(index);
		array.removeAt(index);
		array.insert(target, control);
	}

	// ─── Related services ───

	get relatedServiceIds(): number[] {
		return this.serviceForm.get('related_service_ids')?.value ?? [];
	}

	/** Every other active service — a service can't be related to itself. */
	relatedServiceOptions(): Service[] {
		return this.allServices().filter((s) => s.id !== this.serviceId);
	}

	isRelated(id: number): boolean {
		return this.relatedServiceIds.includes(id);
	}

	toggleRelated(id: number) {
		const current = this.relatedServiceIds;
		const next = current.includes(id)
			? current.filter((existing) => existing !== id)
			: [...current, id];
		this.serviceForm.patchValue({ related_service_ids: next });
	}

	/**
	 * Returns a message naming the first detail row that has some content but
	 * is missing the part the page needs to render it, or null if all rows are
	 * either complete or entirely empty.
	 */
	private findIncompleteDetailRow(): string | null {
		const values = this.serviceForm.value;

		const concerns = values.concerns ?? [];
		for (let i = 0; i < concerns.length; i++) {
			if (!concerns[i]?.title?.trim() && concerns[i]?.description?.trim()) {
				return `Concern ${i + 1} has a description but no title.`;
			}
		}

		const steps = values.how_it_works ?? [];
		for (let i = 0; i < steps.length; i++) {
			if (!steps[i]?.title?.trim() && steps[i]?.description?.trim()) {
				return `Step ${i + 1} has a description but no title.`;
			}
		}

		const faqs = values.faqs ?? [];
		for (let i = 0; i < faqs.length; i++) {
			const question = faqs[i]?.question?.trim();
			const answer = faqs[i]?.answer?.trim();
			if (question && !answer) {
				return `FAQ ${i + 1} is missing an answer.`;
			}
			if (!question && answer) {
				return `FAQ ${i + 1} is missing a question.`;
			}
		}

		return null;
	}

	/**
	 * Pre-fill the repeaters with the number of rows the website copy doc
	 * uses for every service page: 5 reasons, 4 concerns, 4 steps, 5 FAQs.
	 */
	private seedDetailBlocks() {
		for (let i = 0; i < 5; i++) this.addWhyUsPoint();
		for (let i = 0; i < 4; i++) this.addConcern();
		for (let i = 0; i < 4; i++) this.addStep();
		for (let i = 0; i < 5; i++) this.addFaq();
	}

	private loadAllServices() {
		this.http.get<any>(`${environment.apiUrl}/services`).subscribe({
			next: (response) => {
				if (response.status && response.data) {
					this.allServices.set(response.data);
				}
			},
		});
	}

	/** Replace a FormArray's contents with rows built from saved data. */
	private fillArray(array: FormArray, items: any[], build: (item: any) => void) {
		array.clear();
		(items ?? []).forEach(build);
	}

	private patchDetailFields(service: any) {
		this.serviceForm.patchValue(
			{
				meta_title: service.meta_title ?? '',
				meta_description: service.meta_description ?? '',
				intro: service.intro ?? '',
				why_us_title: service.why_us_title ?? '',
				concerns_title: service.concerns_title ?? '',
				how_it_works_title: service.how_it_works_title ?? '',
				who_its_for: service.who_its_for ?? '',
				related_service_ids: service.related_service_ids ?? [],
				cta_text: service.cta_text ?? '',
				cta_button_label: service.cta_button_label ?? '',
			},
			{ emitEvent: false },
		);

		this.fillArray(this.whyUsPoints, service.why_us_points, (point) =>
			this.addWhyUsPoint(point),
		);
		this.fillArray(this.concerns, service.concerns, (concern) => this.addConcern(concern));
		this.fillArray(this.howItWorks, service.how_it_works, (step) => this.addStep(step));
		this.fillArray(this.faqs, service.faqs, (faq) => this.addFaq(faq));
	}

	updatePreview(values: any) {
		this.previewData.set({
			service_name: values.service_name || 'Service Name',
			short_description: values.short_description || 'Short description will appear here',
			description: values.description || 'Detailed description will appear here',
			pricing: values.pricing || 0,
			image_path: this.imagePreview() || '',
		});
	}

	onFileSelected(event: any) {
		const file = event.target.files[0];
		if (file) {
			// Validate file type
			if (!file.type.startsWith('image/')) {
				this.errorMessage.set('Please select an image file');
				return;
			}

			// Validate file size (5MB)
			if (file.size > 5 * 1024 * 1024) {
				this.errorMessage.set('Image size should not exceed 5MB');
				return;
			}

			// Open cropper modal
			this.selectedImageForCrop = file;
			event.target.value = '';
			this.errorMessage.set('');
			setTimeout(() => this.imageCropper.open(), 0);
		}
	}

	onImageCropped(event: { file: File; preview: string }) {
		this.selectedFile = event.file;
		this.serviceForm.patchValue({ image: event.file });
		this.serviceForm.get('image')?.markAsTouched();
		this.imagePreview.set(event.preview);
		this.updatePreview(this.serviceForm.value);
		this.selectedImageForCrop = null;
	}

	onImageCropCancelled() {
		this.selectedImageForCrop = null;
	}

	removeImage() {
		this.imagePreview.set(null);
		this.selectedFile = null;
		this.serviceForm.patchValue({ image: null });
		this.updatePreview(this.serviceForm.value);

		// If creating new service, image is required
		if (!this.isEditMode()) {
			this.serviceForm.get('image')?.setValidators(Validators.required);
			this.serviceForm.get('image')?.updateValueAndValidity();
		}
	}

	submitForm() {
		// Mark all fields as touched to show validation errors
		Object.keys(this.serviceForm.controls).forEach((key) => {
			this.serviceForm.get(key)?.markAsTouched();
		});

		if (this.serviceForm.invalid) {
			this.errorMessage.set('Please fill all required fields correctly');
			return;
		}

		// Detail content is optional, but a half-filled row would be dropped
		// silently on save — so catch it here rather than losing the copy.
		const incomplete = this.findIncompleteDetailRow();
		if (incomplete) {
			this.errorMessage.set(incomplete);
			return;
		}

		// For edit mode, if no new image is selected and we have an existing image, it's valid
		if (this.isEditMode() && !this.selectedFile && this.imagePreview()) {
			// Valid, proceed without image
		} else if (!this.selectedFile) {
			this.errorMessage.set('Please select an image');
			return;
		}

		this.isSubmitting.set(true);
		this.errorMessage.set('');
		this.successMessage.set('');

		const formData = new FormData();
		formData.append('category_id', this.serviceForm.value.category_id);
		formData.append('service_name', this.serviceForm.value.service_name);
		formData.append('tagline', this.serviceForm.value.tagline);
		formData.append('short_description', this.serviceForm.value.short_description);
		formData.append('description', this.serviceForm.value.description);
		formData.append('pricing', this.serviceForm.value.pricing || 0);
		formData.append('duration_minutes', this.serviceForm.value.duration_minutes);
		formData.append('is_consultation', this.serviceForm.value.is_consultation ? '1' : '0');
		formData.append('is_featured', this.serviceForm.value.is_featured ? '1' : '0');
		formData.append('display_order', this.serviceForm.value.display_order);

		// ─── Detail page content ───
		// Repeaters go over as JSON because the request is multipart
		// (it carries the image upload); the API decodes them before validating.
		const values = this.serviceForm.value;
		const textFields = [
			'meta_title',
			'meta_description',
			'intro',
			'why_us_title',
			'concerns_title',
			'how_it_works_title',
			'who_its_for',
			'cta_text',
			'cta_button_label',
		];
		textFields.forEach((field) => formData.append(field, values[field] ?? ''));

		// Drop rows the admin left blank rather than saving empty cards.
		const points = (values.why_us_points ?? []).filter((p: string) => p?.trim());
		const concerns = (values.concerns ?? []).filter((c: any) => c?.title?.trim());
		const steps = (values.how_it_works ?? []).filter((s: any) => s?.title?.trim());
		const faqs = (values.faqs ?? []).filter((f: any) => f?.question?.trim() && f?.answer?.trim());

		formData.append('why_us_points', JSON.stringify(points));
		formData.append('concerns', JSON.stringify(concerns));
		formData.append('how_it_works', JSON.stringify(steps));
		formData.append('faqs', JSON.stringify(faqs));
		formData.append('related_service_ids', JSON.stringify(values.related_service_ids ?? []));

		if (this.selectedFile) {
			formData.append('image', this.selectedFile);
		}

		const apiUrl = this.isEditMode()
			? `${environment.apiUrl}/services/${this.serviceId}`
			: `${environment.apiUrl}/services`;

		const request = this.http.post<any>(apiUrl, formData);

		request.subscribe({
			next: (response) => {
				this.isSubmitting.set(false);
				if (response.status) {
					this.successMessage.set(
						this.isEditMode()
							? 'Service updated successfully!'
							: 'Service added successfully!',
					);
					setTimeout(() => {
						this.router.navigate(['/admin/services']);
					}, 1500);
				} else {
					this.errorMessage.set(response.message || 'Operation failed');
				}
			},
			error: (error) => {
				console.error('Error:', error);
				this.isSubmitting.set(false);
				this.errorMessage.set(error.error?.message || 'An error occurred');
			},
		});
	}

	cancel() {
		this.router.navigate(['/admin/services']);
	}

	isFieldInvalid(fieldName: string): boolean {
		const field = this.serviceForm.get(fieldName);
		return !!(field && field.invalid && field.touched);
	}

	getImageUrl(path: string): string {
		if (path.startsWith('http') || path.startsWith('data:')) {
			return path;
		}
		return `${environment.storageUrl}/${path}`;
	}

	onImageError(event: any) {
		event.target.src =
			'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2VhZWFlNyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgYXZhaWxhYmxlPC90ZXh0Pjwvc3ZnPg==';
	}
}
